import PersonIcon from "@mui/icons-material/Person"
import Typography from "@mui/material/Typography"
import { DateTime } from "luxon"
import React, { RefObject, useRef } from "react"

import {
  STATUS_CANCELLED,
  STATUS_CONFIRMED,
  STATUS_NO_SHOW,
  STATUS_PENDING,
  STATUS_WALK_IN,
  STATUS_SEATED,
  STATUS_REMOVED,
} from "@/constants/reservations"
import { GetReservationsQuery } from "@/graphql/graphql"

export type CalendarViewReservation = {
  position: {
    top: number
    left: number
    width: number
    height: number
  }
  startTime: DateTime
  endTime: DateTime
} & GetReservationsQuery["venue"]["reservations"]["nodes"][number]

export type OnReservationFocusCallback = (reservation: CalendarViewReservation, target: HTMLDivElement) => void

interface ReservationProps {
  reservation: CalendarViewReservation
  onReservationFocus?: OnReservationFocusCallback
}

const STATUS_COLORS = {
  [STATUS_PENDING]: {
    container: "bg-r-pending border-r-pending",
    ribbon: "bg-r-pending-dark",
  },
  [STATUS_CONFIRMED]: {
    container: "bg-r-confirmed border-r-confirmed-dark",
    ribbon: "bg-r-confirmed-dark",
  },
  [STATUS_SEATED]: {
    container: "bg-r-seated border-r-seated-dark",
    ribbon: "bg-r-seated-dark",
  },
  [STATUS_NO_SHOW]: {
    container: "bg-r-no-show border-r-no-show",
    ribbon: "bg-r-no-show-dark",
  },
  [STATUS_WALK_IN]: {
    container: "bg-r-walk-in border-r-walk-in-dark",
    ribbon: "bg-r-walk-in-dark",
  },
  [STATUS_CANCELLED]: {
    container: "bg-r-cancelled border-r-cancelled",
    ribbon: "bg-r-cancelled-dark",
  },
  [STATUS_REMOVED]: {
    container: "bg-r-removed border-r-removed",
    ribbon: "bg-r-removed-dark",
  },
}

const Reservation: React.FC<ReservationProps> = ({ reservation, onReservationFocus }) => {
  const ref = useRef() as RefObject<HTMLDivElement>

  const statusStyles = STATUS_COLORS[reservation.status] || {
    container: "bg-gray-400 border-gray-600",
    ribbon: "bg-gray-600",
  }

  return (
    <div
      className="absolute flex items-stretch justify-stretch cursor-pointer pointer-events-auto"
      style={{ ...reservation.position }}
      onClick={() => onReservationFocus?.(reservation, ref.current)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onReservationFocus?.(reservation, ref.current)
        }
      }}
      role="button"
      tabIndex={0}
      data-testid={`panel-reservations-calendar-reservation-${reservation.id}`}
      ref={ref}
    >
      <div
        className={`${statusStyles.container} border border-solid rounded-md w-full mx-1 overflow-hidden text-white flex flex-col h-full`}
      >
        <div className={`${statusStyles.ribbon} h-2 px-1`} />
        <div className="p-1 pb-2 flex flex-col flex-1">
          <div className="flex-grow">
            {reservation.customerContact?.name ? (
              <Typography variant="subtitle1" className="text-white leading-5">
                {reservation.customerContact.name}
              </Typography>
            ) : null}
            {reservation.peopleAmount != null && (
              <div className="flex items-center gap-1">
                <PersonIcon className="text-white" fontSize="inherit" />
                {reservation.peopleAmount}
              </div>
            )}
          </div>

          <Typography variant="body2" className="text-xs text-center">
            {reservation.startTime.toLocaleString(DateTime.TIME_24_SIMPLE)} -{" "}
            {reservation.endTime.toLocaleString(DateTime.TIME_24_SIMPLE)}{" "}
          </Typography>
        </div>
      </div>
    </div>
  )
}

export default Reservation
